import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { normalizeMixboxes, normalizeOffers, setPricingRules, getPricingRules, setTradeMode } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";

const normalize = (items) => normalizeOffers(normalizeMixboxes(items));
import { api } from "@/lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    // Re-normalise saved carts from their real composition so quantity tiers and
    // the established mixed-size saving never depend on stored client prices.
    try { return normalize(JSON.parse(localStorage.getItem("dl_cart") || "[]")); }
    catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("dl_cart", JSON.stringify(items));
  }, [items]);

  const [shipCountry, setShipCountry] = useState(() => localStorage.getItem("dl_ship_country") || "United Kingdom");
  useEffect(() => {
    localStorage.setItem("dl_ship_country", shipCountry);
  }, [shipCountry]);

  const [discountCode, setDiscountCode] = useState(() => localStorage.getItem("dl_discount_code") || "");
  useEffect(() => {
    if (discountCode) localStorage.setItem("dl_discount_code", discountCode);
    else localStorage.removeItem("dl_discount_code");
  }, [discountCode]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      if (item.type === "mixbox") return normalize([...prev, item]);
      const existing = prev.find((p) => p.key === item.key);
      const next = existing
        ? prev.map((p) => p.key === item.key ? { ...p, quantity: p.quantity + item.quantity } : p)
        : [...prev, item];
      return normalize(next);
    });
  }, []);

  const updateQty = useCallback((key, quantity) => {
    if (quantity < 1) return;
    setItems((prev) => normalize(prev.map((p) => p.key === key ? { ...p, quantity } : p)));
  }, []);

  const updateItem = useCallback((key, patch) => {
    setItems((prev) => normalize(prev.map((p) => p.key === key ? { ...p, ...patch } : p)));
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => normalize(prev.filter((p) => p.key !== key))), []);
  const clear = useCallback(() => { setItems([]); setDiscountCode(""); }, []);

  const [pricingRules, setRulesState] = useState(getPricingRules());
  useEffect(() => {
    api.get("/pricing-rules").then((r) => { setPricingRules(r.data); setRulesState(r.data); setItems((prev) => normalize([...prev])); }).catch(() => {});
  }, []);

  // Trade (dropship) accounts get volume-tiered mix-bundle savings; re-price the cart when role changes.
  const { user } = useAuth();
  useEffect(() => {
    setTradeMode(user?.role === "dropship");
    setItems((prev) => normalize([...prev]));
  }, [user?.role]);

  const [bumpKey, setBumpKey] = useState(0);
  const bumpCart = useCallback(() => setBumpKey((k) => k + 1), []);

  const value = useMemo(() => {
    const lineTotal = (i) => ((i.type === "mixbox" || i.type === "economy") ? (i.box_price ?? i.unit_price * (i.bottle_count || 1)) : i.unit_price) * i.quantity;
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
    return { items, addItem, updateQty, updateItem, removeItem, clear, count, subtotal, bumpKey, bumpCart, shipCountry, setShipCountry, pricingRules, discountCode, setDiscountCode };
  }, [items, addItem, updateQty, updateItem, removeItem, clear, bumpKey, bumpCart, shipCountry, pricingRules, discountCode]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
